'use client';

import React, { useRef, useState, useCallback, useMemo, type FC, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import BottomSheets from './bottom-sheets';
import type { BottomSheetsMethods } from './bottom-sheets.types';
import Fragment from '../../molecules/fragment';
import { Modal } from '../../molecules/modal/modal';
import { useIsDesktop } from '../../utils/use-media-query.hook';
import type {
    AppBottomSheetAction,
    AppBottomSheetInjectedProps,
    AppBottomSheetComponentProps,
    AppBottomSheetHeaderProps,
    AppBottomSheetExtraParamsProps,
    AppBottomSheetFooterProps,
    AppBottomSheetBackdropProps,
} from './with-app-bottom-sheet.types';
import { cn } from '../../utils/cn';
import { BOTTOM_SHEET_HEIGHT, type BottomSheetHeight } from './bottom-sheets.constants';
/**
 * Higher-Order Component that provides bottom sheet functionality matching mobile's withAppBottomSheet.
 *
 * This HOC wraps a component and provides a `bottomSheet` object with:
 * - `inject(view, headerProps, extraProps, footerProps, backdropProps)` - Inject content
 * - `show()` - Present the bottom sheet
 * - `close()` - Close the bottom sheet
 *
 * @example
 * ```tsx
 * const MyContent = ({ bottomSheet }) => {
 *   const handleOpen = () => {
 *     bottomSheet.inject(
 *       <div>Content</div>,
 *       { title: 'My Title' },
 *       { scrollEnabled: true },
 *       undefined,
 *       { pressBehavior: 'close' }
 *     );
 *     bottomSheet.show();
 *   };
 *
 *   return <button onClick={handleOpen}>Open Sheet</button>;
 * };
 *
 * const MyComponent = withAppBottomSheet(MyContent);
 * ```
 *
 * @param WrappedComponent - The component to wrap with bottom sheet functionality
 * @returns A new component with bottomSheet prop injected
 */
export function withAppBottomSheet<P extends object>(
    WrappedComponent: FC<P & AppBottomSheetInjectedProps>
): React.FC<P & AppBottomSheetComponentProps> {
    const ComponentWithBottomSheet: React.FC<P & AppBottomSheetComponentProps> = props => {
        const {
            snapPoints: _defaultSnapPoints = ['75%'],
            displayMode = 'auto',
            desktopModalSize = 'medium',
            desktopBreakpoint = 1024,
            ...rest
        } = props;

        // Responsive detection
        const isDesktopViewport = useIsDesktop(desktopBreakpoint);

        // Determine which component to show based on displayMode and viewport
        const shouldShowModal = useMemo((): boolean => {
            if (displayMode === 'modal') return true;
            if (displayMode === 'sheet') return false;
            // 'auto' mode: modal on desktop, sheet on mobile
            return isDesktopViewport;
        }, [displayMode, isDesktopViewport]);

        // Bottom sheet ref (for sheet mode)
        const sheetRef = useRef<BottomSheetsMethods>(null);

        // Modal state (for modal mode)
        const [isModalOpen, setIsModalOpen] = useState(false);

        // Injected content state
        const [content, setContent] = useState<ReactNode>(null);
        const [headerProps, setHeaderProps] = useState<AppBottomSheetHeaderProps>();
        const [extraProps, setExtraProps] = useState<AppBottomSheetExtraParamsProps>();
        const [footerProps, setFooterProps] = useState<AppBottomSheetFooterProps>();
        const [_backdropProps, setBackdropProps] = useState<AppBottomSheetBackdropProps>();

        /**
         * Inject content into the bottom sheet/modal
         */
        const inject = useCallback(
            (
                view: ReactNode,
                hProps?: AppBottomSheetHeaderProps,
                extra?: AppBottomSheetExtraParamsProps,
                footer?: AppBottomSheetFooterProps,
                bProps?: AppBottomSheetBackdropProps
            ) => {
                setContent(view);
                setHeaderProps(hProps);
                setExtraProps(extra);
                setFooterProps(footer);
                setBackdropProps(bProps);
            },
            []
        );

        /**
         * Show/present the bottom sheet or modal
         */
        const show = useCallback(() => {
            if (shouldShowModal) {
                setIsModalOpen(true);
            } else {
                sheetRef.current?.open();
            }
        }, [shouldShowModal]);

        /**
         * Close the bottom sheet or modal
         */
        const close = useCallback(() => {
            if (shouldShowModal) {
                setIsModalOpen(false);
            } else {
                sheetRef.current?.close();
            }
        }, [shouldShowModal]);

        /**
         * Bottom sheet action object matching mobile API
         */
        const bottomSheet: AppBottomSheetAction = useMemo(
            () => ({
                inject,
                show,
                close,
            }),
            [inject, show, close]
        );

        // Build injected props
        const injectedProps = {
            ...rest,
            bottomSheet,
        } as P & AppBottomSheetInjectedProps;

        // Determine container class based on scroll/height config
        const containerClassName = cn(
            extraProps?.scrollEnabled && 'app-bottom-sheet-scrollable',
            extraProps?.containerClassName
        );

        // Handle modal close
        const handleModalClose = useCallback(() => {
            setIsModalOpen(false);
            headerProps?.onClose?.();
        }, [headerProps]);

        const handleSheetClose = useCallback(() => {
            headerProps?.onClose?.();
        }, [headerProps]);

        // Use portal to render overlays at document.body level,
        // escaping any ancestor stacking contexts (e.g. will-change, transform, z-index)
        // that would trap position:fixed elements.
        const portalTarget = typeof document !== 'undefined' ? document.body : null;
        const isBottomSheetHeight = (value: unknown): value is BottomSheetHeight => {
            return Object.values(BOTTOM_SHEET_HEIGHT).includes(value as BottomSheetHeight);
        };

        const resolvedHeight: BottomSheetHeight | undefined = isBottomSheetHeight(extraProps?.height)
            ? extraProps.height
            : undefined;
        const overlayContent = (
            <Fragment>
                {/* Render Modal for desktop */}
                {shouldShowModal && (
                    <Modal
                        open={isModalOpen}
                        onClose={handleModalClose}
                        title={headerProps?.title}
                        titleProps={headerProps?.titleProps}
                        className={containerClassName}
                        size={desktopModalSize}
                        closeOnOverlayClick={true}
                        showCloseButton={true}
                        footer={footerProps?.component}
                    >
                        {extraProps?.customHeader}
                        {content}
                    </Modal>
                )}

                {/* Render BottomSheet for mobile */}
                {!shouldShowModal && (
                    <BottomSheets
                        ref={sheetRef}
                        title={headerProps?.title}
                        titleProps={headerProps?.titleProps}
                        footer={footerProps?.component}
                        dismissOnBackdrop={true}
                        onClose={handleSheetClose}
                        containerClassName={containerClassName}
                        childrenClassName={extraProps?.childrenClassName}
                        height={resolvedHeight}
                    >
                        {extraProps?.customHeader}
                        {content}
                    </BottomSheets>
                )}
            </Fragment>
        );

        return (
            <Fragment>
                <WrappedComponent {...injectedProps} />
                {portalTarget ? createPortal(overlayContent, portalTarget) : overlayContent}
            </Fragment>
        );
    };

    ComponentWithBottomSheet.displayName = `withAppBottomSheet(${
        WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return ComponentWithBottomSheet;
}

export default withAppBottomSheet;
